import { supabase } from '@/lib/supabase/client';
import { isAuthApiError, type Session, type User } from '@supabase/supabase-js';

export type SignInErrorReason =
  | 'invalid_credentials'
  | 'user_not_found'
  | 'rate_limited'
  | 'unknown';

export interface SignInErrorResult {
  status: 'error';
  reason: SignInErrorReason;
  /**
   * Debug information that should never be surfaced to end users. Consumers may log this output
   * in development to help trace Supabase responses without leaking sensitive details.
   */
  context?: {
    status?: number;
    message?: string;
  };
}

export interface SignInSuccessResult {
  status: 'success';
  user: User;
  session: Session | null;
}

export type SignInResult = SignInErrorResult | SignInSuccessResult;

export interface SignInCredentials {
  email: string;
  password: string;
}

const INVALID_MESSAGES = new Set(['Invalid login credentials', 'Invalid email or password']);

const createErrorResult = (
  reason: SignInErrorResult['reason'],
  context: SignInErrorResult['context'] = {},
): SignInErrorResult => ({
  status: 'error',
  reason,
  context,
});

const mapSupabaseAuthError = (error: unknown): SignInErrorResult => {
  if (isAuthApiError(error)) {
    if (INVALID_MESSAGES.has(error.message) || error.status === 400 || error.status === 401) {
      return createErrorResult('invalid_credentials', {
        status: error.status,
      });
    }
    if (error.status === 404) {
      return createErrorResult('user_not_found', { status: error.status });
    }
    if (error.status === 429) {
      return createErrorResult('rate_limited', { status: error.status });
    }
    return createErrorResult('unknown', {
      status: error.status,
      message: error.message,
    });
  }

  if (error instanceof Error) {
    return createErrorResult('unknown', { message: error.message });
  }

  return createErrorResult('unknown');
};

export async function signInWithPassword(credentials: SignInCredentials): Promise<SignInResult> {
  const { email, password } = credentials;
  const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });

  if (error) {
    return mapSupabaseAuthError(error);
  }

  return {
    status: 'success',
    user: signInData.user,
    session: signInData.session ?? null,
  };
}
